import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import patientAvatar from "./icons/avatar.png";
import { api } from "services/ApiService";
import { getUserId } from "auth/authenticate";
import { tConvert } from "helpers/timeUtils";
import { ApiEndpoint, Method } from "config/constants/apiConstants";

import "./index.css";

const MAX_FEATURED = 15;

/* ---------- helpers ---------- */

const isDoctorActive = (doctor) => {
  if (!doctor) return false;

  // Backend may use any of these names — check all before giving up.
  const flag = doctor.isActive ?? doctor.is_active ?? doctor.active;
  if (flag === false) return false;

  const status = String(doctor?.status || "").toUpperCase();
  if (status === "INACTIVE" || status === "SUSPENDED") return false;

  return true;
};

const timeSlotValidation = (time) => {
  const now = new Date();
  const slot = new Date(
    `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}, ${time}:00`,
  );
  return slot.getTime() >= now.getTime() + 60 * 60 * 1000;
};

/**
 * Returns the earliest available slot across the days in `dataArr`.
 *
 * Note: the previous implementation overwrote `avail` on every iteration,
 * so only the LAST day's slots ever won. This version returns on the first
 * day that has any available slot — which is what "Next Available" should
 * actually mean.
 */
const findNextAvailable = (dataArr) => {
  if (!Array.isArray(dataArr)) return null;

  for (let index = 0; index < dataArr.length; index++) {
    const day = dataArr[index];
    if (!day) continue;

    const collect = (bucket) =>
      (Array.isArray(bucket) ? bucket : [])
        .filter((slot) => {
          if (!slot || slot.reserved) return false;
          // Only enforce the "not in the next hour" rule on today's slots.
          if (index === 0 && process.env.REACT_APP_ENV !== "DEV") {
            return timeSlotValidation(slot.time);
          }
          return true;
        })
        .map((slot) => ({ time: slot.time, date: day.date }));

    const morning = collect(day.morning);
    if (morning.length) return morning[0];

    const evening = collect(day.evening);
    if (evening.length) return evening[0];
  }

  return null;
};

/* ---------- component ---------- */

const FeatureDoctor = () => {
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api(
          `${process.env.REACT_APP_BACKEND_URL}user/patient/featured-doctors/${getUserId()}`,
        );
        if (active) {
          setDoctors(Array.isArray(response?.data) ? response.data : []);
        }
      } catch (err) {
        console.error("Unable to load featured doctors", err);
        if (active) setError("Unable to load featured doctors.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const featured = useMemo(
    () => doctors.filter(isDoctorActive).slice(0, MAX_FEATURED),
    [doctors],
  );

  const reload = async () => {
    try {
      const response = await api(
        `${process.env.REACT_APP_BACKEND_URL}user/patient/featured-doctors/${getUserId()}`,
      );
      setDoctors(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error("Unable to reload featured doctors", err);
    }
  };

  const addToFavourites = async (id) => {
    const result = await api(
      ApiEndpoint.ADD_TO_FAVOURITE_DOCTORS + id,
      Method.GET,
      null,
      null,
    );
    if (result) await reload();
  };

  const removeFromFavourites = async (id) => {
    const result = await api(
      ApiEndpoint.REMOVE_FROM_FAVOURITE_DOCTORS + id,
      Method.DELETE,
      null,
      null,
    );
    if (result) await reload();
  };

  const renderHeader = () => (
    <div className="featureDoctorHeading">
      <h4>Featured doctors</h4>
      <Link to="/doctors" style={{ textDecoration: "none" }}>
        <Button
          variant="outlined"
          size="small"
          endIcon={<NavigateNextIcon />}
          sx={{ textTransform: "none" }}
        >
          See all
        </Button>
      </Link>
    </div>
  );

  if (loading) {
    return (
      <div className="FeatureDoctorMain">
        {renderHeader()}
        <div className="featuredDoctorsState">
          <CircularProgress size={24} />
          <span>Loading doctors…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="FeatureDoctorMain">
        {renderHeader()}
        <div className="featuredDoctorsState featuredDoctorsState--error">
          <p>{error}</p>
          <Button variant="outlined" size="small" onClick={reload}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!featured.length) {
    return (
      <div className="FeatureDoctorMain">
        {renderHeader()}
        <div className="featuredDoctorsState">
          <p>No doctors available right now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="FeatureDoctorMain">
      {renderHeader()}

      <div className="featuredDoctorsGrid">
        {featured.map((doctor) => {
          const next = findNextAvailable(doctor?.nextAppointment);

          return (
            <Paper
              key={doctor?.doctorId}
              elevation={1}
              className="doctorCard"
            >
              {/* Favorite toggle */}
              <IconButton
                size="small"
                aria-label={
                  doctor?.isFavourite
                    ? "Remove from favourites"
                    : "Add to favourites"
                }
                className="doctorCardFavorite"
                onClick={() =>
                  doctor?.isFavourite
                    ? removeFromFavourites(doctor?.doctorId)
                    : addToFavourites(doctor?.doctorId)
                }
              >
                {doctor?.isFavourite ? (
                  <FavoriteIcon fontSize="small" sx={{ color: "#fe8244" }} />
                ) : (
                  <FavoriteBorderIcon
                    fontSize="small"
                    sx={{ color: "#52627f" }}
                  />
                )}
              </IconButton>

              {/* Header */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  alt={doctor?.name || "Doctor"}
                  src={doctor?.User?.profileImagePath || patientAvatar}
                  variant="rounded"
                  className="doctorAvatar"
                />

                <Box minWidth={0} flex={1}>
                  <h5 className="doctorCardName textCapitalization">
                    {doctor?.name || "Doctor"}
                  </h5>

                  <p className="doctorCardCategories">
                    {(doctor?.serviceCategories || []).join(" · ") ||
                      "Specialist"}
                  </p>
                </Box>
              </Stack>

              {/* Meta row */}
              <div className="doctorCardMeta">
                {doctor?.experienceYears > 0 && (
                  <span>{doctor.experienceYears}+ yrs experience</span>
                )}
                {doctor?.patientCount > 0 && (
                  <>
                    {doctor?.experienceYears > 0 && (
                      <span className="doctorCardMetaDot" aria-hidden="true" />
                    )}
                    <span>
                      {doctor.patientCount}{" "}
                      {doctor.patientCount === 1 ? "patient" : "patients"}
                    </span>
                  </>
                )}
              </div>

              {/* Next available */}
              {next && (
                <div className="doctorCardNext">
                  <div className="doctorCardNextLabel">Next available</div>
                  <div className="doctorCardNextTime">
                    {tConvert(next.time)} · {next.date}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="doctorCardActions">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(`/doctors/${doctor?.doctorId}`)}
                  sx={{ textTransform: "none" }}
                >
                  Add appointment
                </Button>
              </div>
            </Paper>
          );
        })}
      </div>
    </div>
  );
};

export default FeatureDoctor;